import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocale } from '../../contexts/LocaleContext';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../UI/dialog';
import { themeClasses } from '../../config/themeClasses';
import { cn } from '../../lib/utils';

const selectClassName =
  'flex h-10 w-full rounded-lg border border-border-light bg-background-secondary px-3 py-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

const textareaClassName =
  'flex min-h-[80px] w-full rounded-lg border border-border-light bg-background-secondary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

const EditWatchlistItemModal = ({ item, open, onClose, onSubmit }) => {
  const isOpen = open ?? Boolean(item);
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const safeItem = item || {};

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      notes: safeItem.notes || '',
      priority: safeItem.priority || 'normal',
      alertEnabled: safeItem.alertEnabled || false,
      portStartPoint: safeItem.portStartPoint || '',
      portEndPoint: safeItem.portEndPoint || '',
    },
  });

  if (!item) {
    return null;
  }

  const handleFormSubmit = async (data) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  const addedDate = safeItem.addedDate
    ? new Date(safeItem.addedDate).toLocaleDateString()
    : t('unknownDate');

  return (
    <Dialog open={isOpen} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-md" closeLabel={t('dialogCloseLabel')}>
        <DialogHeader>
          <DialogTitle>{t('editWatchlistItemTitle')}</DialogTitle>
          <DialogDescription>{t('editWatchlistItemDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
          <div className={cn('rounded-lg p-4', themeClasses.appKit.emptyDashed, 'text-left py-4')}>
            <h3 className="text-sm font-medium text-text-primary mb-1">
              {t('editWatchlistCompanyLabel', {
                companyId: safeItem.companyId || t('unknownCompany'),
              })}
            </h3>
            <p className="text-xs text-text-secondary">
              {t('editWatchlistAddedOn', { date: addedDate })}
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="notes" className="text-sm font-medium text-text-primary">
              {t('notesLabel')}
            </label>
            <textarea
              id="notes"
              {...register('notes')}
              rows={3}
              className={textareaClassName}
              placeholder={t('notesPlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="priority" className="text-sm font-medium text-text-primary">
              {t('priority')}
            </label>
            <select
              id="priority"
              {...register('priority', { required: t('priorityRequired') })}
              className={selectClassName}
            >
              <option value="low">{t('priorityLow')}</option>
              <option value="normal">{t('priorityNormal')}</option>
              <option value="high">{t('priorityHigh')}</option>
            </select>
            {errors.priority && (
              <p className="text-sm text-danger-500">{errors.priority.message}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="alertEnabled"
              type="checkbox"
              {...register('alertEnabled')}
              className="h-4 w-4 rounded border-border-light text-primary-500 focus:ring-primary-500"
            />
            <label htmlFor="alertEnabled" className="text-sm text-text-secondary">
              {t('enableAlertsForCompany')}
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="portStartPoint" className="text-sm font-medium text-text-primary">
                {t('watchlistStartPoint')}
              </label>
              <Input
                id="portStartPoint"
                {...register('portStartPoint')}
                placeholder={t('portStartPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="portEndPoint" className="text-sm font-medium text-text-primary">
                {t('watchlistEndPoint')}
              </label>
              <Input
                id="portEndPoint"
                {...register('portEndPoint')}
                placeholder={t('portEndPlaceholder')}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  {t('updatingLabel')}
                </span>
              ) : (
                t('updateWatchlistItem')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditWatchlistItemModal;
